/**
 * Event service for API communication with event endpoints.
 */

import apiClient from '../api/client';
import { Event, EventCreate, EventUpdate, EventListResponse, LAST_EVENT_ID_KEY } from '../types/event';

export const eventService = {
    /**
     * Get all events for the current account.
     */
    async getEvents(skip: number = 0, limit: number = 100): Promise<EventListResponse> {
        const response = await apiClient.get<EventListResponse>('/api/v1/events', {
            params: { skip, limit },
        });
        return response.data;
    },

    /**
     * Get a specific event by ID.
     */
    async getEvent(eventId: string): Promise<Event> {
        const response = await apiClient.get<Event>(`/api/v1/events/${eventId}`);
        return response.data;
    },

    /**
     * Create a new event.
     */
    async createEvent(eventData: EventCreate): Promise<Event> {
        const response = await apiClient.post<Event>('/api/v1/events', eventData);
        return response.data;
    },

    /**
     * Update an existing event.
     */
    async updateEvent(eventId: string, eventData: EventUpdate): Promise<Event> {
        const response = await apiClient.patch<Event>(`/api/v1/events/${eventId}`, eventData);
        return response.data;
    },

    /**
     * Soft delete an event.
     */
    async deleteEvent(eventId: string): Promise<void> {
        await apiClient.delete(`/api/v1/events/${eventId}`);
    },

    /**
     * Store the last active event ID in localStorage.
     */
    setLastEventId(eventId: string): void {
        localStorage.setItem(LAST_EVENT_ID_KEY, eventId);
    },

    /**
     * Get the last active event ID from localStorage.
     */
    getLastEventId(): string | null {
        return localStorage.getItem(LAST_EVENT_ID_KEY);
    },

    /**
     * Clear the last active event ID from localStorage.
     */
    clearLastEventId(): void {
        localStorage.removeItem(LAST_EVENT_ID_KEY);
    },
};

export default eventService;
